/** @fileoverview Renders queued prompts and their steering/removal actions. */

import { CornerUpRight, Trash2 } from 'lucide-react';
import type { QueuedChatMessage } from '../../types/chat';
import { Shimmer } from '../ai-elements/shimmer';
import { Message, MessageContent } from '../ai-elements/message';
import { Button } from '../ui/button';

type QueuedMessagesProps = {
  messages: QueuedChatMessage[];
  onSteer: (id: string) => void;
  onRemove: (id: string) => void;
};

export default function QueuedMessages({ messages, onSteer, onRemove }: QueuedMessagesProps) {
  if (!messages.length) return null;

  return (
    <div className="mb-2 flex flex-col gap-2" role="status" aria-live="polite">
      {messages.map((message, index) => (
        <Message className="ml-auto w-fit min-w-0 max-w-4xl gap-1" from="user" key={message.id}>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-0">
              <CornerUpRight className="mt-1 h-5 w-5 animate-pulse" aria-hidden="true" />
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-6 shrink-0 px-1 text-xs"
                aria-label={`${message.status === 'steering' ? 'Queue' : 'Send now'} message ${
                  index + 1
                }`}
                disabled={message.status === 'steering'}
                onClick={() => onSteer(message.id)}
              >
                {message.status === 'steering' ? (
                  'Queue'
                ) : (
                  <Shimmer as="span" className="text-xs">
                    Send now
                  </Shimmer>
                )}
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-app-text-muted">
                {message.status === 'steering' ? 'Sending' : 'Queued'}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove queued message ${index + 1}`}
                onClick={() => onRemove(message.id)}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          </div>
          <MessageContent className="w-full min-w-0 wrap-anywhere">
            {message.text ||
              (message.attachmentCount
                ? `${message.attachmentCount} ${message.attachmentCount === 1 ? 'file' : 'files'}`
                : 'Selected elements')}
          </MessageContent>
        </Message>
      ))}
    </div>
  );
}
